/**
 * Weather-layer rendering — rain / snow / fog overlays (CLIENT_DESIGN §7).
 *
 * M2.1 rewrite: particle rendering is now driven by `WeatherState.precip_type`
 * and `WeatherState.precip_intensity` from the tick snapshot, rather than the
 * discrete `WeatherKind` alone. Particle count scales proportionally to
 * intensity. Rain = vertical streaks, Snow = drifting dots, Sleet = both.
 *
 * Per-particle shelter masking (rain stops under roofs) is a deferred item.
 * Fog overlay, cloud shadows, and wind vegetation effects are also deferred.
 */

import { Graphics } from 'pixi.js';

import type { WeatherState } from '../world/model';
import type { SceneLayers } from './scene';

const MAX_RAIN_PARTICLES = 400;
const MAX_SNOW_PARTICLES = 250;
const RAIN_SPEED_PX = 10;
const HEAVY_RAIN_SPEED_PX = 14;
const HEAVY_RAIN_SLANT = 3;
const SNOW_FALL_SPEED = 1.2;
const SNOW_DRIFT_AMPLITUDE = 0.8;

interface Particle {
  x: number;
  y: number;
  drift?: number;
}

export interface WeatherRenderer {
  render: (
    weather: WeatherState,
    viewportW: number,
    viewportH: number,
  ) => void;
  destroy: () => void;
}

export function createWeatherRenderer(layers: SceneLayers): WeatherRenderer {
  const g = new Graphics();
  layers.weatherLayer.addChild(g);
  g.eventMode = 'none';

  const rain = initParticles(MAX_RAIN_PARTICLES);
  const snow = initParticles(MAX_SNOW_PARTICLES, true);

  const render = (
    weather: WeatherState,
    viewportW: number,
    viewportH: number,
  ): void => {
    g.clear();
    g.visible = true;

    const intensity = Math.max(0, Math.min(1, weather.precip_intensity));
    const precip = weather.precip_type;

    // No precipitation — hide layer.
    if (intensity <= 0 || precip === 'None') {
      // Also handle the legacy kind-only path: Clear/Wind → nothing.
      if (weather.kind === 'Clear' || weather.kind === 'Wind') {
        g.visible = false;
        return;
      }
      // Fog fallback for legacy kind-only weather states.
      if (weather.kind === 'Fog') {
        g.rect(0, 0, viewportW, viewportH);
        g.fill({ color: 0xd0d4d7, alpha: 0.15 + 0.25 * Math.max(0.3, weather.fog_density) });
        return;
      }
      g.visible = false;
      return;
    }

    // Rain (includes Sleet — render rain streaks + snow dots).
    if (precip === 'Rain' || precip === 'Sleet') {
      const count = Math.floor(MAX_RAIN_PARTICLES * intensity);
      const isHeavy = intensity > 0.6;
      const speed = isHeavy ? HEAVY_RAIN_SPEED_PX : RAIN_SPEED_PX;
      const slant = isHeavy ? HEAVY_RAIN_SLANT : 0;
      stepRain(rain, viewportW, viewportH, speed, slant);
      drawRain(g, rain, count, slant, intensity);
    }

    // Snow (or Sleet — layer snow dots on top of rain).
    if (precip === 'Snow' || precip === 'Sleet') {
      const count = Math.floor(MAX_SNOW_PARTICLES * intensity);
      stepSnow(snow, viewportW, viewportH);
      drawSnow(g, snow, count, intensity);
    }
  };

  return {
    render,
    destroy: () => {
      g.destroy();
    },
  };
}

function initParticles(count: number, withDrift = false): Particle[] {
  const arr: Particle[] = [];
  for (let i = 0; i < count; i++) {
    arr.push({
      x: Math.random() * 2000 - 500,
      y: Math.random() * 1200 - 200,
      ...(withDrift ? { drift: Math.random() * Math.PI * 2 } : {}),
    });
  }
  return arr;
}

function stepRain(
  particles: Particle[],
  w: number,
  h: number,
  speed: number,
  slant: number,
): void {
  for (const p of particles) {
    p.y += speed;
    p.x += slant;
    if (p.y > h + 20) {
      p.y = -20;
      p.x = Math.random() * (w + 200) - 100;
    } else if (p.x > w + 40) {
      p.x -= w + 80;
    }
  }
}

function drawRain(
  g: Graphics,
  particles: Particle[],
  count: number,
  slant: number,
  alpha: number,
): void {
  const n = Math.min(count, particles.length);
  for (let i = 0; i < n; i++) {
    const p = particles[i]!;
    g.moveTo(p.x, p.y);
    g.lineTo(p.x + slant, p.y + 8);
  }
  g.stroke({ color: 0xaecde8, alpha: 0.4 + 0.4 * alpha, width: 1 });
}

function stepSnow(particles: Particle[], w: number, h: number): void {
  for (const p of particles) {
    p.y += SNOW_FALL_SPEED;
    const drift = p.drift ?? 0;
    p.x += Math.sin(p.y * 0.02 + drift) * SNOW_DRIFT_AMPLITUDE;
    if (p.y > h + 10) {
      p.y = -10;
      p.x = Math.random() * (w + 100) - 50;
    }
  }
}

function drawSnow(
  g: Graphics,
  particles: Particle[],
  count: number,
  alpha: number,
): void {
  const n = Math.min(count, particles.length);
  for (let i = 0; i < n; i++) {
    const p = particles[i]!;
    g.circle(p.x, p.y, 1.6);
  }
  g.fill({ color: 0xffffff, alpha: 0.55 + 0.4 * alpha });
}
